'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import { toast } from 'sonner';

const categories = [
{ id: 'cat-all', label: 'All' },
{ id: 'cat-wellness', label: 'Wellness' },
{ id: 'cat-beauty', label: 'Beauty' },
{ id: 'cat-personal-care', label: 'Personal Care' },
{ id: 'cat-lifestyle', label: 'Lifestyle' }];


const featuredProducts = [
{
  id: 'prod-001',
  name: 'Lumière Radiance Serum',
  category: 'beauty',
  price: 89,
  memberPrice: 72,
  pv: 45,
  badge: 'Best Seller',
  badgeColor: 'bg-primary/10 text-primary',
  image: "https://images.unsplash.com/photo-1632127649343-1f27f424bf0b",
  alt: 'Elegant glass serum bottle with gold dropper on white marble surface',
  inStock: true
},
{
  id: 'prod-002',
  name: 'VitaCore Daily Essentials',
  category: 'wellness',
  price: 65,
  memberPrice: 52,
  pv: 35,
  badge: 'New',
  badgeColor: 'bg-success-bg text-success',
  image: "https://images.unsplash.com/photo-1700911758948-f2990ca7df8f",
  alt: 'Premium supplement bottles arranged on clean white background with green leaf accent',
  inStock: true
},
{
  id: 'prod-003',
  name: 'Éclat Brightening Mask',
  category: 'beauty',
  price: 48,
  memberPrice: 38,
  pv: 25,
  badge: null,
  badgeColor: '',
  image: "https://images.unsplash.com/photo-1532413301822-a5fe62c07854",
  alt: 'Luxurious face mask jar with pearl finish on soft pink background',
  inStock: true
},
{
  id: 'prod-004',
  name: 'Harmonie Wellness Tea',
  category: 'wellness',
  price: 34,
  memberPrice: 27,
  pv: 18,
  badge: 'Popular',
  badgeColor: 'bg-violet-100 text-violet-700 dark:bg-violet-950/50 dark:text-violet-400',
  image: "https://images.unsplash.com/photo-1613405617284-0e7b527ccb8d",
  alt: 'Premium herbal tea collection in elegant packaging with loose leaf herbs scattered around',
  inStock: true
},
{
  id: 'prod-005',
  name: 'Silk Body Ritual Oil',
  category: 'personal-care',
  price: 56,
  memberPrice: 45,
  pv: 30,
  badge: null,
  badgeColor: '',
  image: "https://images.unsplash.com/photo-1629384136243-640bf5eb8f51",
  alt: 'Amber glass bottle of body oil surrounded by botanical ingredients on wooden surface',
  inStock: false
},
{
  id: 'prod-006',
  name: 'Equilibre Balance Complex',
  category: 'wellness',
  price: 78,
  memberPrice: 62,
  pv: 40,
  badge: 'Featured',
  badgeColor: 'bg-blue-100 text-blue-700 dark:bg-blue-950/50 dark:text-blue-400',
  image: "https://images.unsplash.com/photo-1560553086-4184bd89f392",
  alt: 'Sophisticated supplement complex with clinical packaging on gradient background',
  inStock: true
},
{
  id: 'prod-007',
  name: 'Maison Lifestyle Candle',
  category: 'lifestyle',
  price: 42,
  memberPrice: 34,
  pv: 22,
  badge: null,
  badgeColor: '',
  image: "https://images.unsplash.com/photo-1594360439343-3f62887f4df6",
  alt: 'Luxury scented candle in matte black vessel on marble surface with soft candlelight',
  inStock: true
},
{
  id: 'prod-008',
  name: 'Pur Cleanse Foam Wash',
  category: 'personal-care',
  price: 38,
  memberPrice: 30,
  pv: 20,
  badge: 'New',
  badgeColor: 'bg-success-bg text-success',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_10ac1dfcb-1772216203794.png",
  alt: 'Minimalist cleanser bottle in frosted glass with botanical extract drops',
  inStock: true
}];


export default function ProductShowcase() {
  const [activeCategory, setActiveCategory] = useState('cat-all');
  const [wishlist, setWishlist] = useState<string[]>([]);

  const filtered = activeCategory === 'cat-all' ?
  featuredProducts :
  featuredProducts.filter((p) => `cat-${p.category}` === activeCategory);

  const toggleWishlist = (id: string) => {
    setWishlist((prev) =>
    prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleAddToCart = (name: string) => {
    toast.success(`${name} added to cart`, {
      description: 'Your selection has been added to your shopping cart.'
    });
  };

  return (
    <section className="py-24 lg:py-32 bg-secondary">
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <span className="section-divider" />
              <span className="text-xs font-semibold tracking-widest uppercase text-primary">Our Products</span>
            </div>
            <h2 className="font-display text-section-title text-foreground">
              Curated for Excellence
            </h2>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {categories.map((cat) =>
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
              activeCategory === cat.id ?
              'bg-primary text-primary-foreground shadow-gold' :
              'bg-card text-muted-foreground border border-border hover:border-primary hover:text-primary'}`
              }>

                {cat.label}
              </button>
            )}
          </div>
        </div>

        {/* Product grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {filtered.map((product) =>
          <div
            key={product.id}
            className="product-card card-elevated card-hover overflow-hidden group">

              {/* Image */}
              <div className="relative overflow-hidden bg-muted" style={{ aspectRatio: '4/5' }}>
                <AppImage
                src={product.image}
                alt={product.alt}
                fill
                sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
                className="product-card-img object-cover" />

                {/* Overlay */}
                <div className="absolute inset-0 bg-accent/0 group-hover:bg-accent/20 transition-colors duration-300" />

                {/* Badge */}
                {product.badge &&
              <span className={`absolute top-3 left-3 richesse-badge text-xs ${product.badgeColor}`}>
                    {product.badge}
                  </span>
              }

                {/* Stock */}
                {!product.inStock &&
              <div className="absolute inset-0 bg-background/60 flex items-center justify-center">
                    <span className="text-sm font-semibold text-muted-foreground bg-card px-3 py-1.5 rounded-full border border-border">
                      Out of Stock
                    </span>
                  </div>
              }

                {/* Wishlist */}
                <button
                onClick={() => toggleWishlist(product.id)}
                className="absolute top-3 right-3 w-8 h-8 rounded-full bg-card/90 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-200 hover:bg-card shadow-sm"
                aria-label="Add to wishlist">

                  <Icon
                  name={wishlist.includes(product.id) ? 'HeartIcon' : 'HeartIcon'}
                  size={16}
                  className={wishlist.includes(product.id) ? 'text-rose-500' : 'text-muted-foreground'}
                  variant={wishlist.includes(product.id) ? 'solid' : 'outline'} />

                </button>
              </div>

              {/* Info */}
              <div className="p-4">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1.5 capitalize">
                  {product.category.replace('-', ' ')}
                </p>
                <h3 className="text-sm font-semibold text-foreground mb-3 leading-snug">{product.name}</h3>

                {/* Pricing */}
                <div className="flex items-baseline gap-2 mb-4">
                  <span className="text-lg font-bold text-foreground tabular-nums">${product.memberPrice}</span>
                  <span className="text-xs text-muted-foreground line-through tabular-nums">${product.price}</span>
                  <span className="text-xs font-medium text-primary ml-auto">{product.pv} PV</span>
                </div>

                {/* CTA removed */}
              </div>
            </div>
          )}
        </div>

        {/* View all */}
        <div className="text-center mt-12">
          <Link href="/product-catalog" className="btn-outline-gold inline-flex items-center gap-2 px-8 py-3.5 rounded-xl text-sm font-semibold">
            View All Products
            <Icon name="ArrowRightIcon" size={16} />
          </Link>
        </div>
      </div>
    </section>);

}